import { useMemo } from "react";
import { Box, GlobalStyles, IconButton, Tooltip } from "@mui/material";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import type { ColDef, ICellRendererParams } from "ag-grid-enterprise";
import { AgGridReact } from "ag-grid-react";
import { globalDefaultColDef } from "@/setup/ag-grid/mesf-theme";
import {
	NUMBER_CELL_CLASS,
	ONE_LINE_CELL_CLASS,
	OneLineCellStyles,
} from "@/controls/ag-grid/OneLineCells";
import type { PassStatusOption, RollsProductionRow } from "../models/Rollshop";

const ROW_HEIGHT = 30;
const HEADER_HEIGHT = 32;

// Same height for every rollset (eight passes; more scroll inside), so switching rollsets does not resize the panel.
export const PASS_CONDITION_HEIGHT = HEADER_HEIGHT + 8 * ROW_HEIGHT + 2;

const exceedsSchedule = (row: RollsProductionRow) =>
	(row.RolledTonnage ?? 0) > (row.ScheduledTonnage ?? 0);

// The procedure repeats a pass when it is laid out on several stands; the grid shows it once.
const onePerPosition = (rows: RollsProductionRow[]) => {
	const seen = new Map<number, RollsProductionRow>();
	for (const row of rows) {
		if (row.PassPosition != null && !seen.has(row.PassPosition))
			seen.set(row.PassPosition, row);
	}
	return [...seen.values()].sort(
		(a, b) => (a.PassPosition ?? 0) - (b.PassPosition ?? 0),
	);
};

interface Props {
	rows: RollsProductionRow[];
	passStatus: PassStatusOption[];
	/** False while the rollset is In use: the pass status is then read only. */
	editable: boolean;
	lockedReason?: string;
	onEdit: (row: RollsProductionRow) => void;
}

export const PassConditionGrid = ({
	rows,
	passStatus,
	editable,
	lockedReason = "",
	onEdit,
}: Props) => {
	const rowData = useMemo(() => onePerPosition(rows), [rows]);

	const columnDefs = useMemo<ColDef<RollsProductionRow>[]>(() => {
		const statusName = (code: string | null) =>
			passStatus.find((s) => s.Code === code)?.Name ?? code ?? "";

		return [
			{
				field: "PassPosition",
				headerName: "#",
				width: 44,
				cellClass: NUMBER_CELL_CLASS,
			},
			{
				field: "PassName",
				headerName: "Pass",
				flex: 1.2,
				minWidth: 80,
				cellClass: ONE_LINE_CELL_CLASS,
			},
			{
				field: "PassTypeName",
				headerName: "Type",
				flex: 0.8,
				minWidth: 64,
				cellClass: ONE_LINE_CELL_CLASS,
			},
			{
				field: "PassShapeName",
				headerName: "Shape",
				flex: 1,
				minWidth: 72,
				cellClass: ONE_LINE_CELL_CLASS,
			},
			{
				colId: "Tonnage",
				headerName: "Sched / Rolled",
				width: 130,
				valueGetter: (p) =>
					p.data
						? `${(p.data.ScheduledTonnage ?? 0).toFixed(2)} / ${(p.data.RolledTonnage ?? 0).toFixed(2)}`
						: "",
				tooltipValueGetter: (p) =>
					p.data && exceedsSchedule(p.data)
						? "Exceeds scheduled production"
						: undefined,
				// Two numbers in one cell: centered under the header, digits still tabular.
				cellClass: [ONE_LINE_CELL_CLASS, "dxt-tabular"],
				cellClassRules: {
					"dxt-exceeds-schedule": (p) => !!p.data && exceedsSchedule(p.data),
				},
			},
			{
				field: "PassLastChangeReasonName",
				headerName: "Last Change",
				flex: 1,
				minWidth: 84,
				cellClass: ONE_LINE_CELL_CLASS,
			},
			{
				field: "PassStatusCode",
				headerName: "Status",
				width: 96,
				valueFormatter: (p) => statusName(p.value),
				cellClass: ONE_LINE_CELL_CLASS,
			},
			{
				colId: "edit",
				headerName: "",
				width: 44,
				resizable: false,
				cellRenderer: (p: ICellRendererParams<RollsProductionRow>) => {
					const row = p.data;
					if (!row) return null;
					// Rows of a rollset without production have no record to update.
					const reason = lockedReason
						? lockedReason
						: !editable
							? "The rollset is In use."
							: row.IdBottom == null && row.IdTop == null
								? "This pass has no production records yet."
								: "";
					return (
						<Box
							sx={{
								display: "flex",
								alignItems: "center",
								justifyContent: "center",
								height: "100%",
							}}
						>
							<Tooltip title={reason || "Edit pass condition"}>
								<span>
									<IconButton
										size="small"
										sx={{ p: 0.25 }}
										disabled={reason !== ""}
										onClick={() => onEdit(row)}
										aria-label="edit pass condition"
									>
										<EditOutlinedIcon fontSize="small" />
									</IconButton>
								</span>
							</Tooltip>
						</Box>
					);
				},
			},
		];
	}, [passStatus, editable, lockedReason, onEdit]);

	const defaultColDef = useMemo(
		() => ({
			...globalDefaultColDef,
			wrapText: false,
			autoHeight: false,
			sortable: false,
			resizable: true,
		}),
		[],
	);

	return (
		<Box sx={{ height: "100%" }}>
			<OneLineCellStyles />
			<GlobalStyles
				styles={{
					".ag-cell.dxt-tabular": { fontVariantNumeric: "tabular-nums" },
					".ag-cell.dxt-exceeds-schedule": {
						color: "#d32f2f",
						fontWeight: 600,
					},
				}}
			/>
			<AgGridReact<RollsProductionRow>
				rowData={rowData}
				columnDefs={columnDefs}
				defaultColDef={defaultColDef}
				// A turn creates new production records: the id makes the row, and its select, new.
				getRowId={(p) =>
					`${p.data.PassPosition}-${p.data.IdBottom ?? p.data.IdTop ?? "none"}`
				}
				suppressCellFocus
				suppressContextMenu
				animateRows={false}
				rowHeight={ROW_HEIGHT}
				headerHeight={HEADER_HEIGHT}
				enableBrowserTooltips
				overlayNoRowsTemplate="There are no production records."
			/>
		</Box>
	);
};
